import {Component, ElementRef, inject, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {DesignSystemModule} from "../../../design-system/design-system.module";
import {DiagramBreadcrumbModule} from "../diagram-breadcrumb/diagram-breadcrumb.module";
import {DirectivesModule} from "../../../../directives/directives.module";
import {CommonModule, NgForOf, NgIf} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {InfrastructureSchemaService, RouterNetworkTree} from "../../../../services/back/infrastructure-schema.service";
import {finalize, forkJoin, from, Observable, of, Subscription, switchMap} from "rxjs";
import {CurrentTenantService} from "../../../../services/front/current-tenant.service";
import {map, tap} from "rxjs/operators";
import {Grid} from "./three/Shapes/Grid";
import {setRenderer} from "./three/System/SceneConfig";
import {Shapes} from "./three/ShapeManager/ManagerShape";
import {LineLevels} from "./three/Shapes/LineLevels";
import {levelArray, ManagerLevel} from "./three/ShapeManager/ManagerLevel";
import {InteractionsAndControls, setupInteractions} from "./three/System/InteractionsAndControls";
import {camera, disposeCamera, onRecenter, onZoomIn, onZoomOut, switchCameraView, updateCameraTransition} from "./three/System/Camera";
import {updateAllLOD} from "./three/System/LODManager";
import {MatRipple} from "@angular/material/core";
import {LoadingProgressComponent} from "./three/loadingBar";
import {Object3D, Vector3} from "three";
import {processInfrastructure,} from "./three/System/SortData";
import {calculateRouterPlacements, CalculateTotalSize, RouterNodeOutput} from "./three/System/GlobalSizing";
import {ServerDetailComponent, ServerDetailInput} from "../../infrastructure/server/server-detail/server-detail.component";
import {RightSliderService} from "../../../../services/front/right-slider.service";
import {
	InfrastructureSchemasInteractionServerService,
	InfrastructureSchemasInteractionService,
	ModelClick
} from "../../../../services/front/infrastructure-schemas-interaction.service";
import {RouterDetailComponent, RouterDetailInput} from "../../infrastructure/router/router-detail/router-detail.component";
import {
	NetworkDetailComponent,
	NetworkDetailInput
} from "../../infrastructure/network/network-detail/network-detail.component";
import {CameraSnapshotManager} from "./three/System/SnapShot";
import {Server} from "../../../../services/model/server.model";
import { PopupService } from './three/popup-service';
import * as THREE from 'three';
import {INFRASTRUCTURE_URL} from "../../../../models/home/navigation.model";
import {IntercomService} from "../../../../services/front/intercom.service";
import {NavigationExtras, Router} from "@angular/router";

@Component({
	selector: 'app-diagram-infrastructure',
	imports: [
		DesignSystemModule,
		DiagramBreadcrumbModule,
		DirectivesModule,
		CommonModule,
		TranslateModule,
		LoadingProgressComponent,
		MatRipple,
	],
	templateUrl: './diagram-infrastructure.component.html',
	styleUrl: './diagram-infrastructure.component.scss'
})
export class DiagramInfrastructureComponent implements OnInit, OnDestroy{

	tenantId: string;

	_loading: boolean;
	_refreshing: boolean;
	isEmpty: boolean = false;

	data: RouterNetworkTree[] = [];
	isData: boolean = false;
	isNotData: boolean = false;

	subscription: Subscription = new Subscription();

	private currentTenantService: CurrentTenantService = inject(CurrentTenantService);
	private infrastructureSchemasService: InfrastructureSchemaService = inject(InfrastructureSchemaService);

	@ViewChild('canvasContainer', { static: true }) canvasContainer: ElementRef<HTMLElement>;

	width: number = 0;
	height: number = 0;

	private rightSliderService: RightSliderService = inject(RightSliderService);
	private infrastructureSchemasInteractionService: InfrastructureSchemasInteractionService = inject(InfrastructureSchemasInteractionService)
	private infrastructureSchemasServerService: InfrastructureSchemasInteractionServerService = inject(InfrastructureSchemasInteractionServerService)
	private popupService: PopupService = inject(PopupService);
	private intercomService: IntercomService = inject(IntercomService);
	private activePopupButton: Object3D | null = null;

	private grid: Grid;
	private levelInstances: LineLevels[];
	private renderer: THREE.WebGLRenderer;
	private scene: THREE.Scene;

	private shouldAnimate = true;
	private cameraControls: InteractionsAndControls;
	private snapshotManager: CameraSnapshotManager;
	private disposeInteractionsFn: () => void;

	public horizontalScrollPosition: number = 50;
	public verticalScrollPosition: number = 50;
	public showHorizontalScrollbar: boolean = true;
	public showVerticalScrollbar: boolean = true;
	private isHorizontalDragging: boolean = false;
	private isVerticalDragging: boolean = false;
	private startDragPosition: { x: number, y: number } = { x: 0, y: 0 };
	private startScrollPosition: { horizontal: number, vertical: number } = { horizontal: 0, vertical: 0 };

	serversToDisplay: Server[] = [];
	popupX: number = 0;
	popupY: number = 0;
	isPopupVisible: boolean = false;

	constructor(public router: Router) {
	}

	// Initialisation
	ngOnInit() {
		this.subscription.add(this.currentTenantService.getCurrentTenantIdChanges()
			.pipe(tap(tenantId => this.tenantId = tenantId))
			.subscribe(() => this.initialize()));
		this.setupWheelEventHandler();

		document.addEventListener('mousemove', this.onDocumentMouseMove.bind(this));
		document.addEventListener('mouseup', this.onDocumentMouseUp.bind(this));

		this.subscription.add(this.infrastructureSchemasInteractionService.click$.pipe(
			switchMap((modelClick: ModelClick) => {
				if (modelClick.type === "Router") {
					const data: RouterDetailInput = {routerId: modelClick.id};
					return this.rightSliderService.openComponent(RouterDetailComponent, data)
				} else if (modelClick.type === "Network") {
					const data: NetworkDetailInput = {networkId: modelClick.id};
					return this.rightSliderService.openComponent(NetworkDetailComponent, data)
				} else {
					const data: ServerDetailInput = {serverId: modelClick.id};
					return this.rightSliderService.openComponent(ServerDetailComponent, data)
				}
			})
		).subscribe())

		// Initialisation du popup
		setTimeout(() => {
			this.popupService.initialize(this.canvasContainer.nativeElement, camera);
		});
		this.subscription.add(
			this.popupService.popupPosition$.subscribe(position => {
				this.popupX = position.x;
				this.popupY = position.y;
				this.isPopupVisible = position.isVisible;
			})
		);
		this.subscription.add(
			this.popupService.serversList$.subscribe(servers => {
				this.serversToDisplay = servers;
			})
		);
		this.subscription.add(
			this.infrastructureSchemasServerService.click$.subscribe(servers => {
				this.findPlusButtonInScene()
				// let foundButton = null;
				// scene.traverse((object) => {
				// 	if (object.userData && object.userData.modelType === 'PlusServer') {
				// 		foundButton = object;
				// 	}
				// });
				// this.activePopupButton = foundButton;
				this.popupService.showExtraServers(servers);
			})
		);

		// Création de la scène
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color(0xf7f8fA);
		this.scene.backgroundIntensity = 0;

		// Création du renderer
		this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
	}

	initialize(): void {
		this.subscription.add(this.switchLoading()
			.pipe(
				switchMap(() => this.infrastructureSchemasService.getGlobalInfrastructureSchema(this.tenantId)),
				tap((data)=> this.setDiagramInfrastructureData(data)),
				finalize(() => this.switchLoading()))
			.subscribe(() => this.setCreateSchema()));
	}

	private switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	private setupWheelEventHandler(): void {
		setTimeout(() => {
			const container = this.canvasContainer.nativeElement;
			if (container) {
				container.addEventListener('wheel', this.onMouseWheel, { passive: false });
			}
		});
	}

	private onMouseWheel = (event: WheelEvent): void => {
		event.preventDefault();
		this.closeServerList()
	}

	private setDiagramInfrastructureData(data: RouterNetworkTree[]): void {
		this.data = data;
	}

	private setCreateSchema(): void {

		if (this.data.length < 1) {
			this.isNotData = true;
			return;
		}

		this.isData = true
		setRenderer(this.canvasContainer, this.renderer);

		const sortedData: RouterNetworkTree[] = processInfrastructure(this.data)
		const dataguide: RouterNodeOutput[] = calculateRouterPlacements(sortedData);

		this.grid = new Grid(this.scene)
		this.levelInstances = ManagerLevel(this.scene, sortedData, this.canvasContainer.nativeElement.clientWidth);

		const sizeMax: { xMin: number, xMax: number, zMin: number, zMax: number } = CalculateTotalSize(dataguide, levelArray);

		Shapes(this.scene, sortedData, dataguide)

		// Initialisation des contrôles et interactions
		const result = setupInteractions(this.canvasContainer, this.infrastructureSchemasInteractionService, this.infrastructureSchemasServerService, this.scene, this.renderer);
		this.cameraControls = result.cameraControls;
		this.disposeInteractionsFn = result.disposeInteractions;

		// Définir les limites de la caméra en utilisant les bornes calculées
		this.cameraControls.setBoundsFromCalculatedSize(sizeMax, this.scene);

		// Configurer le callback pour mettre à jour les scrollbars
		this.cameraControls.onCameraMove = (xPercent: number, zPercent: number) => {
			if (xPercent < 0 || zPercent < 0) {
				this.showHorizontalScrollbar = false;
				this.showVerticalScrollbar = false;
			} else {
				this.showHorizontalScrollbar = true;
				this.showVerticalScrollbar = true;
				this.horizontalScrollPosition = xPercent * 100;
				this.verticalScrollPosition = zPercent * 100;
			}
		};

		this.onRecenter()
		if (!this.shouldAnimate) {
			this.shouldAnimate = true;
		}
		this.animate();

		this.snapshotManager = new CameraSnapshotManager(this.renderer, camera, this.scene);
	}

	private noDataSchema(): void {

	}

	private animate(): void {
		if (!this.shouldAnimate) return;

		requestAnimationFrame(() => this.animate());

		// Obtenir les dimensions actuelles du conteneur
		const containerWidth = this.canvasContainer.nativeElement.clientWidth;
		const containerHeight = this.canvasContainer.nativeElement.clientHeight;

		if (this.renderer.domElement.width !== containerWidth || this.renderer.domElement.height !== containerHeight) {
			this.renderer.setSize(containerWidth, containerHeight);
		}

		const aspect = containerWidth / containerHeight;

		if (Math.abs((camera.right - camera.left) / (camera.top - camera.bottom) - aspect) > 0.01) {
			const frustumHeight = Math.abs(camera.top - camera.bottom);
			const halfFrustumWidth = (frustumHeight * aspect) / 2;

			camera.left = -halfFrustumWidth;
			camera.right = halfFrustumWidth;
			camera.updateProjectionMatrix();
		}

		// Mettre à jour les animations et transitions
		updateCameraTransition();
		this.grid.update(camera.position);
		this.levelInstances.forEach(line => {
			line.update(camera.position);
		});
		updateAllLOD(camera);

		// this.updatePopupPositionIfVisible();

		// Effectuer le rendu
		this.renderer.render(this.scene, camera);
	}

	getZoomLevel(): number {
		return this.cameraControls ? this.cameraControls.getZoomPercentage() : 100;
	}

	onRecenter(): void {
		if (this.cameraControls) {
			this.cameraControls.recenter(this.scene);
		} else {
			onRecenter();
		}
	}

	onZoomOut(): void {
		if (this.cameraControls) {
			this.cameraControls.zoomOut();
		} else {
			onZoomOut();
		}
	}

	onZoomIn(): void {
		if (this.cameraControls) {
			this.cameraControls.zoomIn();
		} else {
			onZoomIn();
		}
	}

	changeView(): void {
		switchCameraView()
		this.scene.traverse((child: Object3D): void => {
			if (child.userData && child.userData.type) {
				if (child.userData.type === 'Server' || child.userData.type === 'Icon') {
					child.visible = !child.visible;
				}
			}
		});
	}

	// Méthode pour calculer la taille du thumb horizontal en fonction de la portion visible
	public getHorizontalThumbSize(): number {
		if (!this.cameraControls) return 30; // Valeur par défaut

		// Calculer la portion visible
		const totalWidth = this.cameraControls.cameraBounds.maxX - this.cameraControls.cameraBounds.minX;
		const visibleWidth = (camera.right - camera.left) / camera.zoom;

		// Pourcentage de la scène  visible
		const visiblePercent = Math.min(1, visibleWidth / totalWidth) * 100;

		// Taille minimale entre 10-100%
		return Math.max(10, Math.min(100, visiblePercent));
	}

	// Méthode pour calculer la taille du thumb vertical en fonction de la portion visible
	public getVerticalThumbSize(): number {
		if (!this.cameraControls) return 30; // Valeur par défaut

		// Calculer la portion visible
		const totalHeight = this.cameraControls.cameraBounds.maxZ - this.cameraControls.cameraBounds.minZ;
		const visibleHeight = (camera.top - camera.bottom) / camera.zoom;

		// Pourcentage de la scène visible
		const visiblePercent = Math.min(1, visibleHeight / totalHeight) * 100;

		// Taille minimale entre 10-100%
		return Math.max(10, Math.min(100, visiblePercent));
	}

	// Gestion des scrollbars
	public onScrollThumbMouseDown(event: MouseEvent, type: 'horizontal' | 'vertical'): void {
		event.preventDefault();
		event.stopPropagation();

		this.startDragPosition = { x: event.clientX, y: event.clientY };
		this.startScrollPosition = {
			horizontal: this.horizontalScrollPosition,
			vertical: this.verticalScrollPosition
		};

		if (type === 'horizontal') {
			this.isHorizontalDragging = true;
		} else {
			this.isVerticalDragging = true;
		}
	}

	public onScrollbarTrackClick(event: MouseEvent, type: 'horizontal' | 'vertical'): void {
		event.preventDefault();
		event.stopPropagation();

		const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

		if (type === 'horizontal') {
			const clickPositionX = event.clientX - rect.left;
			const trackWidth = rect.width;
			const thumbSizePercent = this.getHorizontalThumbSize();
			const thumbWidth = trackWidth * (thumbSizePercent / 100);

			const maxPosition = 100 - thumbSizePercent;

			const newPosition = ((clickPositionX - thumbWidth / 2) / (trackWidth - thumbWidth)) * 100;
			this.horizontalScrollPosition = Math.max(0, Math.min(maxPosition, newPosition));

			this.updateCameraFromScrollPosition();
		} else {
			const clickPositionY = event.clientY - rect.top;
			const trackHeight = rect.height;
			const thumbSizePercent = this.getVerticalThumbSize();
			const thumbHeight = trackHeight * (thumbSizePercent / 100);

			const maxPosition = 100 - thumbSizePercent;

			const newPosition = ((clickPositionY - thumbHeight / 2) / (trackHeight - thumbHeight)) * 100;
			this.verticalScrollPosition = Math.max(0, Math.min(maxPosition, newPosition));

			this.updateCameraFromScrollPosition();
		}
	}

	private onDocumentMouseMove(event: MouseEvent): void {
		if (this.isHorizontalDragging) {
			const deltaX = event.clientX - this.startDragPosition.x;
			const trackElement = this.canvasContainer.nativeElement.querySelector('.horizontal-scrollbar .scrollbar-track');
			if (trackElement) {
				const trackWidth = trackElement.clientWidth;

				const movementRatio = deltaX / trackWidth * 100;

				const thumbSizePercent = this.getHorizontalThumbSize();
				const maxPosition = 100 - thumbSizePercent;

				this.horizontalScrollPosition = Math.max(0, Math.min(maxPosition, this.startScrollPosition.horizontal + movementRatio));

				this.updateCameraFromScrollPosition();
			}
		}

		if (this.isVerticalDragging) {
			const deltaY = event.clientY - this.startDragPosition.y;
			const trackElement = this.canvasContainer.nativeElement.querySelector('.vertical-scrollbar .scrollbar-track');
			if (trackElement) {
				const trackHeight = trackElement.clientHeight;

				const movementRatio = deltaY / trackHeight * 100;

				const thumbSizePercent = this.getVerticalThumbSize();
				const maxPosition = 100 - thumbSizePercent;

				this.verticalScrollPosition = Math.max(0, Math.min(maxPosition, this.startScrollPosition.vertical + movementRatio));

				this.updateCameraFromScrollPosition();
			}
		}
	}

	private onDocumentMouseUp(): void {
		this.isHorizontalDragging = false;
		this.isVerticalDragging = false;
	}

	private updateCameraFromScrollPosition(): void {
		if (this.cameraControls) {
			this.cameraControls.moveCameraToScrollPosition(
				this.horizontalScrollPosition / 100,
				this.verticalScrollPosition / 100,
				this.scene
			);
		}
	}

	// Gestion Menu "+"
	findPlusButtonInScene() {
		this.scene.traverse((object) => {
			if (object.userData &&
				object.userData.modelType === 'PlusServer') {
				this.popupService.setPlusButtonObject(object);
			}
		});
	}

	private updatePopupPositionIfVisible(): void {
		// Vérifier si le popup est visible et si nous avons une référence au bouton actif
		if (this.isPopupVisible && this.activePopupButton) {
			// Récupérer la position actuelle du bouton dans l'espace 3D
			const position = new Vector3();
			this.activePopupButton.getWorldPosition(position);

			// Demander au service de recalculer la position d'écran
			this.popupService.updatePositionFromWorldCoords(position);
		}
	}

	openDrawer(id: string): Observable<any> {
		const data: ServerDetailInput = {serverId: id};
		return this.rightSliderService.openComponent(ServerDetailComponent, data)
	}

	searchIcon(type: string | null): string {

		const texturePaths: { [key: string]: string } = {
			directory: 'assets/three/TEXTURE/ICON_ANNUAIRE.png',
			application: 'assets/three/TEXTURE/ICON_APP.png',
			database: 'assets/three/TEXTURE/ICON_BDD.png',
			configuration: 'assets/three/TEXTURE/ICON_CONFIG.png',
			file: 'assets/three/TEXTURE/ICON_FICHIER.png',
			print: '../assets/three/TEXTURE/ICON_IMP.png',
			monitoring: 'assets/three/TEXTURE/ICON_MONITOR.png',
			mail: 'assets/three/TEXTURE/ICON_MSG.png',
			backup: 'assets/three/TEXTURE/ICON_SAVE.png',
			web: 'assets/three/TEXTURE/ICON_WEB.png',
			proxy: 'assets/three/TEXTURE/ICON_PROXY.png',
			security: 'assets/three/TEXTURE/ICON_SECU.png',
		};

		return texturePaths[type || "application"];
	}

	closeServerList(): void {
		this.popupService.hideExtraServers();
		this.activePopupButton = null;
	}

	// Refresh button
	refresh(): void {
		this._refreshing = true;
		this.stopAnimation();
		this.initialize();
		this._refreshing = false;
	}

	// Exporter la carto
	exportDiagram(): void {
		this.snapshotManager.downloadSnapshot('ma-scene-3d');
	}

	// Arrêter le rendu
	private stopAnimation(): void {
		this.shouldAnimate = false;
		while (this.scene.children.length > 0) {
			const child = this.scene.children[0];
			this.scene.remove(child);

			if ((child as any).geometry) {
				(child as any).geometry.dispose?.();
			}
			if ((child as any).material) {
				const material = (child as any).material;
				if (Array.isArray(material)) {
					material.forEach(m => m.dispose?.());
				} else {
					material.dispose?.();
				}
			}
		}

		if (this.disposeInteractionsFn) {
			this.disposeInteractionsFn();
		}

		this.renderer.dispose();
		disposeCamera()

		this.isData = false;
		this.isNotData = false;
	}

	ngOnDestroy() {
		const container = this.canvasContainer?.nativeElement;
		if (container) {
			container.removeEventListener('wheel', this.onMouseWheel);
		}

		// Supprimer les gestionnaires d'événements des scrollbars
		document.removeEventListener('mousemove', this.onDocumentMouseMove.bind(this));
		document.removeEventListener('mouseup', this.onDocumentMouseUp.bind(this));

		this.subscription.unsubscribe();

		this.stopAnimation()

		if (this.renderer.domElement?.parentNode) {
			this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
		}
	}

	goToInfrastructure(): void {
		this.intercomService.hide();
		this.currentTenantService.getCurrentTenantId()
			.pipe(map(tenantId => ({queryParamsHandling: 'preserve', queryParams: { tenant: tenantId }} as NavigationExtras)))
			.subscribe(extras => this.router.navigate([INFRASTRUCTURE_URL], extras));
	}
}
